import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { Action } from '@interstate/components/Action';
import { TrashIcon } from '@interstate/components/Icons';
import { NumericInput } from '@interstate/components/NumericInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@interstate/components/TextInput';
import { ComboBox } from '@interstate/components/ComboBox';

import { useVehicleProtectionContext } from '../../context/VehicleProtectionContext';
import { DATA_TESTID_INPUT_PREFIX } from './constants';
import { getValueFromComboboxChangeEvent } from './utils/getValueFromComboboxChangeEvent';

import { productTypeOptions, profitLocationOptions } from './constants';
import { getValidationMap, PRODUCT_NAME_REQUIRED_MESSAGE } from './validationRules';
import { validateRules, getIsProductInputFocused, getNumericValue } from './utils';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import { getVppProductErrorKey } from '@makemydeal/dr-dash-store/utils/vppProductErrors';

import type { ManualProduct } from '@makemydeal/dr-shared-types';
import type { ManualVppValidationState, ProductValidationState } from './types';
import type { DomEventType } from '@interstate/components/InterstateEvents';

export interface VehicleProtectionManualTableProps {
    products: ManualProduct[];
    onDeleteProduct: (id: string) => void;
    onChangeProduct: (product: ManualProduct, applyProducts?: boolean, skipPayment?: boolean) => void;
    onUpdateProductSingleInput: (value: string | number | undefined, productId: string, inputName: string) => void;
    shouldTriggerValidation?: boolean;
}

export const useManualProductsTable = ({
    products,
    shouldTriggerValidation,
    onDeleteProduct,
    onChangeProduct,
    onUpdateProductSingleInput
}: VehicleProtectionManualTableProps) => {
    const dispatch = useDispatch();

    const isLocked = dealLockHooks.useIsDealLocked();
    const { isDarwinVPPEditDisabled } = useVehicleProtectionContext();
    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);

    const validationMap = useMemo(() => getValidationMap(isOptionalDeskingMode), [isOptionalDeskingMode]);
    const vppProductFieldErrors = useSelector(dealScreenErrorSelectors.getVppProductFieldErrors);

    const [validationState, setValidationState] = useState<ManualVppValidationState>({});
    const [isTouchedState, setIsTouchedState] = useState<Record<string, Record<string, boolean>>>({});

    const buildOption = (value: string) => ({ label: value, value });

    const manualVppProducts = useSelector(dealerSelectors.getManualVppProducts);
    const comboBoxOptions: ComboBoxOptions = useMemo(
        () => (manualVppProducts || []).map((product) => buildOption(product.productName || '')),
        [manualVppProducts]
    );

    const getProductInputError = (productId: string, inputName: string): string | undefined => {
        const productValidationState = validationState[productId] || {};
        const inputValidationState = productValidationState[inputName];
        return inputValidationState?.validationState.message;
    };

    /**
     * The row's own name error is suppressed until the field is touched, so fall back to the stored
     * save-deal error. It is keyed by content, so typing a name stops matching and the message clears.
     */
    const getProductNameError = (product: ManualProduct): string | undefined => {
        const ownError = getProductInputError(product.id, 'productName');
        if (ownError) {
            return ownError;
        }

        // the manual flag only reaches the product once it is stored
        const errorKey = getVppProductErrorKey({ ...product, isManualProduct: true });
        const wasRejectedOnSave = Boolean(errorKey && vppProductFieldErrors[errorKey]?.productName);

        return wasRejectedOnSave ? PRODUCT_NAME_REQUIRED_MESSAGE : undefined;
    };

    const calculateProductValidity = (product: ManualProduct, suppressIfUntouched = true): ProductValidationState => {
        return Object.fromEntries(
            Object.entries(validationMap).map(([inputName, rules]) => {
                const productTouchedState = isTouchedState[product.id] || {};
                const isTouched = productTouchedState[inputName];
                const value = product[inputName as keyof ManualProduct] as string;
                const validationResult = validateRules(value, rules, !isTouched && suppressIfUntouched);
                return [inputName, validationResult];
            })
        );
    };

    const calculateValidationState = (products: ManualProduct[], suppressIfUntouched?: boolean) => {
        return products.reduce((acc, product) => {
            acc[product.id] = calculateProductValidity(product, suppressIfUntouched);
            return acc;
        }, {} as ManualVppValidationState);
    };

    const setTouchedInput = (productId: string, inputName: string) => {
        setIsTouchedState((isTouchedState) => ({
            ...isTouchedState,
            [productId]: {
                ...isTouchedState[productId],
                [inputName]: true
            }
        }));
    };

    useEffect(() => {
        const updatedValidationState = calculateValidationState(products);
        setValidationState(updatedValidationState);
    }, [products]);

    useEffect(() => {
        if (shouldTriggerValidation) {
            const updatedValidationState = calculateValidationState(products, false);
            setValidationState(updatedValidationState);
        }
    }, [shouldTriggerValidation]);

    const updateProductInput = (value: string | number | undefined, productId: string, inputName: string) => {
        onUpdateProductSingleInput(value, productId, inputName);
        setTouchedInput(productId, inputName);
    };

    const updateProduct = (changedProduct: ManualProduct, applyProducts: boolean, skipPayment: boolean) => {
        const productValidity = calculateProductValidity(changedProduct, false);
        const isValid = Object.values(productValidity).every((result) => result.validationState.isValid);

        onChangeProduct(
            {
                ...changedProduct,
                productCost: changedProduct.productCost ?? 0,
                productPrice: changedProduct.productPrice ?? 0,
                isValid
            },
            applyProducts,
            skipPayment
        );
        return changedProduct;
    };

    const applyProduct = (
        value: string | number | undefined,
        product: ManualProduct,
        inputName: string,
        event: DomEventType | undefined
    ) => {
        dispatch(interactionAnalyticsActionCreators.updateAddProtectionProductManuallyTextInput());

        const productInputFocused = getIsProductInputFocused(event);
        const isProductName = inputName === 'productName';
        const shouldSkipPayment = productInputFocused || isProductName;

        const changedProduct = { ...product, [inputName]: value };
        updateProduct(changedProduct, true, shouldSkipPayment);
    };

    const currentProductNames = useRef<Map<string, string>>(new Map());
    const setCurrentProductName = (value: string, productId: string) => {
        currentProductNames.current.set(productId, value);
    };
    /* istanbul ignore next */
    const getCurrentProductName = (productId: string) => {
        return currentProductNames.current.get(productId);
    };

    // Free text reaches the row only here, so commit via applyProduct to refresh the cached `isValid` that
    // gates Save. Falls back to the stored name so blurring without typing cannot clear it.
    const commitProductName = (product: ManualProduct) => {
        setTouchedInput(product.id, 'productName');
        applyProduct(getCurrentProductName(product.id) ?? product.productName, product, 'productName', undefined);
    };

    // Memoize the onDeleteProduct prop to prevent unnecessary re-renders of buildTableDataRow
    const memoizedOnDeleteProduct = useCallback(onDeleteProduct, [onDeleteProduct]);

    // The function is ignored in test coverage as it involves user interaction with ComboBox component, which takes long duration running in tests
    /* istanbul ignore next */
    const handleProductNameComboBoxChange = (selectedProductName: string | undefined, product: ManualProduct) => {
        const selectedProduct = manualVppProducts?.find((p) => p.productName === selectedProductName);

        if (selectedProduct) {
            const updatedProduct: ManualProduct = {
                ...product,
                productName: selectedProduct.productName || '',
                productCategoryCode: selectedProduct.productCategoryCode || '',
                productCode: selectedProduct.productCode || '',
                productCost: selectedProduct.productCost || 0,
                profitLocation: selectedProduct.profitLocation || '',
                productPrice: selectedProduct.productPrice || 0
            };

            updateProduct(updatedProduct, true, false);
            setCurrentProductName(selectedProduct.productName || '', product.id);
        }
    };

    const buildTableDataRow = useCallback(
        (product: ManualProduct) => {
            return {
                productName: (
                    <ComboBox
                        name="productName"
                        size="small"
                        data-testid={`${DATA_TESTID_INPUT_PREFIX.PRODUCT_NAME}${product.id}`}
                        displayLabel={false}
                        options={comboBoxOptions}
                        errorMessage={getProductNameError(product)}
                        hasError={!!getProductNameError(product)}
                        value={[buildOption(product.productName)]}
                        allowNewEntry
                        onChange={
                            // The function is ignored in test coverage as it involves user interaction with ComboBox component, which takes long duration running in tests
                            /* istanbul ignore next */
                            (event) => handleProductNameComboBoxChange(getValueFromComboboxChangeEvent(event), product)
                        }
                        onBlur={
                            // The function is ignored in test coverage as it involves user interaction with ComboBox component, which takes long duration running in tests
                            /* istanbul ignore next */
                            () => commitProductName(product)
                        }
                        onInputChange={(event) => setCurrentProductName(event.target.value, product.id)}
                        disabled={isLocked}
                    />
                ),
                productCategoryCode: (
                    <SelectInput
                        name="productCategoryCode"
                        options={productTypeOptions}
                        displayDeselectOption={false}
                        size="small"
                        data-testid={`${DATA_TESTID_INPUT_PREFIX.PRODUCT_TYPE}${product.id}`}
                        displayLabel={false}
                        value={product.productCategoryCode}
                        onChange={(event) => applyProduct(event.target.value, product, 'productCategoryCode', event.domEvent)}
                        onBlur={(event) => updateProductInput(event.target.value, product.id, 'productCategoryCode')}
                        errorMessage={getProductInputError(product.id, 'productCategoryCode')}
                        hasError={!!getProductInputError(product.id, 'productCategoryCode')}
                        disabled={isLocked}
                    />
                ),
                productCode: (
                    <TextInput
                        name="productCode"
                        size="small"
                        data-testid={`manual-vpp-product-code-${product.id}`}
                        displayLabel={false}
                        value={product.productCode}
                        onBlur={(event) => applyProduct(event.target.value, product, 'productCode', event.domEvent)}
                        onChange={(event) => updateProductInput(event.target.value, product.id, 'productCode')}
                        errorMessage={getProductInputError(product.id, 'productCode')}
                        hasError={!!getProductInputError(product.id, 'productCode')}
                        disabled={isLocked}
                    />
                ),
                productCost: (
                    <NumericInput
                        name="productCost"
                        size="small"
                        data-testid={`${DATA_TESTID_INPUT_PREFIX.PRODUCT_COST}${product.id}`}
                        displayLabel={false}
                        value={product.productCost}
                        onBlur={(event) => applyProduct(getNumericValue(event), product, 'productCost', event.domEvent)}
                        onChange={(event) => updateProductInput(getNumericValue(event), product.id, 'productCost')}
                        decimalMaxLength={2}
                        allowDecimal={true}
                        inputPrefix="$"
                        errorMessage={getProductInputError(product.id, 'productCost')}
                        hasError={!!getProductInputError(product.id, 'productCost')}
                        disabled={isLocked}
                    />
                ),
                profitLocation: (
                    <SelectInput
                        name="profitLocation"
                        size="small"
                        data-testid={`${DATA_TESTID_INPUT_PREFIX.PROFIT_LOCATION}${product.id}`}
                        displayDeselectOption={false}
                        displayLabel={false}
                        options={profitLocationOptions}
                        value={product.profitLocation}
                        onChange={(event) => applyProduct(event.target.value, product, 'profitLocation', event.domEvent)}
                        onBlur={(event) => updateProductInput(event.target.value, product.id, 'profitLocation')}
                        disabled={isLocked}
                    />
                ),
                productPrice: (
                    <NumericInput
                        name="productPrice"
                        size="small"
                        displayLabel={false}
                        data-testid={`${DATA_TESTID_INPUT_PREFIX.PRODUCT_PRICE}${product.id}`}
                        value={product.productPrice}
                        onBlur={(event) => applyProduct(getNumericValue(event), product, 'productPrice', event.domEvent)}
                        onChange={(event) => updateProductInput(getNumericValue(event), product.id, 'productPrice')}
                        decimalMaxLength={2}
                        allowDecimal={true}
                        inputPrefix="$"
                        errorMessage={getProductInputError(product.id, 'productPrice')}
                        hasError={!!getProductInputError(product.id, 'productPrice')}
                        disabled={isLocked}
                    />
                ),
                deleteAction: (
                    <Action
                        actionIcon={{ icon: <TrashIcon />, start: true }}
                        data-testid={`manual-vpp-delete-${product.id}`}
                        onClick={() => memoizedOnDeleteProduct(product.id)}
                        sx={{ paddingTop: '4px' }}
                        disabled={isLocked || isDarwinVPPEditDisabled}
                    />
                )
            };
        },
        [comboBoxOptions, validationState, onDeleteProduct, isDarwinVPPEditDisabled]
    );

    const invalidProductIds = useMemo(() => {
        return Object.entries(validationState)
            .filter(([_, productState]) => {
                const hasInvalidField = Object.values(productState).some((fieldState) => !fieldState.validationState.isValid);
                return hasInvalidField;
            })
            .map(([productId]) => productId);
    }, [validationState]);

    return {
        comboBoxOptions,
        validationState,
        invalidProductIds,
        buildTableDataRow,
        getProductInputError,
        updateProductInput,
        buildOption,
        applyProduct,
        commitProductName,
        handleProductNameComboBoxChange,
        getCurrentProductName,
        setCurrentProductName,
        memoizedOnDeleteProduct,
        isOptionalDeskingMode
    };
};
